// Windy-style animated particle overlay for the velocity/wind vector
// fields, in the spirit of danwild/wind-js-leaflet: a transparent 2D
// canvas drawn on top of the deck.gl map, with particles advected
// through the field and short fading trails instead of arrows.
//
// Field lookup stays mesh-native (no resampling to a raster grid): each
// time the index is (re)built, every currently-loaded vertex for the
// active field/timestep is bucketed into a coarse uniform grid covering
// the current viewport, so per-particle-per-frame lookups are an O(1)
// bucket check + a linear scan over the handful of points in that bucket
// -- not a full scan of the mesh.
import {FILL_VALUE} from './tile-store.js';
import {longitudeInBounds} from './lon-unwrap.js';

// Particle count is fixed regardless of zoom, but the *area* covered by
// the viewport grows enormously when zoomed out, so a count tuned for a
// close-in coastal view reads as nearly empty at continental scale.
// 4000 simple canvas line segments per frame is still cheap for
// Canvas2D, so bias towards "visible when zoomed out" over minimalism.
const PARTICLE_COUNT = 6000;
const MAX_AGE_FRAMES = 260; // how many frames a particle keeps advecting before it's forced to respawn
const GRID_CELLS = 70; // index resolution across the current viewport
const MAX_INDEX_POINTS = 120_000; // decimate very dense views to bound rebuild cost
const LINE_WIDTH = 2.0;
// Fraction of trail alpha kept each frame. This canvas is drawn in
// screen space and never reprojects old segments when the viewport
// changes, so retention this close to 1 (0.99 was tried and looked
// broken) makes panned-away trails visibly stick around for seconds
// instead of fading -- keep this low enough that trails clear out
// within roughly a second of the view moving.
const TRAIL_RETENTION = 0.94;

const SPEED_SCALE = {
  // meters/second -> visual advection scale. Not physically real-time --
  // tuned so motion reads clearly at typical zoom levels.
  velocity: {advect: 55},
  wind: {advect: 12}
};

// Uncolored (no speed-based colormap) -- flat gray at full opacity by
// default, more transparent past HIGH_ZOOM_ZOOM so the traces don't
// overpower the mesh/basemap once you're zoomed in close.
const HIGH_ZOOM_THRESHOLD = 8;
const HIGH_ZOOM_ALPHA = 0.8;
const BASE_ALPHA = 1.0;

function traceColor(alpha) {
  return `rgba(120, 120, 120, ${alpha})`;
}

export class ParticleAnimation {
  constructor({canvas, deckInstance, store, getVisibleTiles, getField, getTimestep}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.deckInstance = deckInstance;
    this.store = store;
    this.getVisibleTiles = getVisibleTiles;
    this.getField = getField;
    this.getTimestep = getTimestep;

    this.particles = [];
    this.index = null; // {buckets, bounds, cellsX, cellsY}
    this.running = false;
    this.rafId = null;
    this._onResize = () => this._resize();
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._resize();
    window.addEventListener('resize', this._onResize);
    this._initParticles();
    this._buildIndex(); // initial build; later updates go through the debounced invalidateIndex()
    this._loop();
  }

  stop() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    clearTimeout(this._rebuildTimer);
    window.removeEventListener('resize', this._onResize);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.particles = [];
    this.index = null;
  }

  // Call whenever the set of loaded vector data could have changed:
  // field switched, timestep changed, or newly-visible tiles finished
  // fetching their vector buffers. Debounced -- at wide zoom, dozens of
  // tiles finish loading in a tight burst, and rebuilding the index (up
  // to MAX_INDEX_POINTS entries) synchronously on every single one of
  // those completions stalls the animation loop. Coalesce them into one
  // rebuild after things settle instead. The render loop keeps drawing
  // with whatever index it already has in the meantime.
  invalidateIndex() {
    clearTimeout(this._rebuildTimer);
    this._rebuildTimer = setTimeout(() => this._buildIndex(), 250);
  }

  _resize() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = window.innerHeight * dpr;
    this.canvas.style.width = `${window.innerWidth}px`;
    this.canvas.style.height = `${window.innerHeight}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  _buildIndex() {
    const field = this.getField();
    const timestep = this.getTimestep();
    const viewport = this.deckInstance.getViewports()[0];
    if (!field || !viewport) {
      this.index = {buckets: new Map(), bounds: null, cellsX: 0, cellsY: 0};
      return;
    }

    const bounds = viewport.getBounds(); // [west, south, east, north]
    const [west, south, east, north] = bounds;
    const cellsX = GRID_CELLS;
    const cellsY = GRID_CELLS;
    const buckets = new Map();

    const byTimestepKey = `${field}ByTimestep`;
    let total = 0;
    const tileEntries = [];
    for (const [z, x, y] of this.getVisibleTiles()) {
      const entry = this.store.get(z, x, y);
      if (!entry || entry.status !== 'geometry-ready') continue;
      const uv = entry[byTimestepKey] && entry[byTimestepKey].get(timestep);
      if (!uv) continue;
      tileEntries.push({entry, uv});
      total += entry.nVerts;
    }

    // decimate uniformly if this would index an unreasonable number of points
    const stride = total > MAX_INDEX_POINTS ? Math.ceil(total / MAX_INDEX_POINTS) : 1;

    for (const {entry, uv} of tileEntries) {
      const pos = entry.positions3;
      const n = entry.nVerts;
      for (let i = 0; i < n; i += stride) {
        const lon = pos[i * 3];
        const lat = pos[i * 3 + 1];
        const u = uv[i * 2];
        const v = uv[i * 2 + 1];
        if (u === FILL_VALUE || v === FILL_VALUE) continue; // dry (velocity only)
        if (!longitudeInBounds(lon, west, east) || lat < south || lat > north) continue;

        const cx = Math.min(cellsX - 1, Math.max(0, Math.floor(((lon - west) / (east - west)) * cellsX)));
        const cy = Math.min(cellsY - 1, Math.max(0, Math.floor(((lat - south) / (north - south)) * cellsY)));
        const key = cy * cellsX + cx;
        const point = {lon, lat, u, v};
        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = [];
          buckets.set(key, bucket);
        }
        bucket.push(point);
      }
    }

    this.index = {buckets, bounds, cellsX, cellsY};
  }

  // Checks the home cell first, then expands outward ring by ring (3x3,
  // 5x5, ...) up to maxRing. Needed because the grid resolution is fixed
  // across the whole viewport: in open-ocean areas where the adaptive
  // mesh is coarse, many cells have zero sample points even though real
  // (sparse) data exists nearby -- without this, particles there would
  // almost always land in an empty cell and be killed on the spot.
  _lookup(lon, lat, maxRing = 6) {
    if (!this.index || !this.index.bounds) return null;
    const {buckets, bounds, cellsX, cellsY} = this.index;
    const [west, south, east, north] = bounds;
    if (!longitudeInBounds(lon, west, east) || lat < south || lat > north) return null;

    const cx = Math.min(cellsX - 1, Math.max(0, Math.floor(((lon - west) / (east - west)) * cellsX)));
    const cy = Math.min(cellsY - 1, Math.max(0, Math.floor(((lat - south) / (north - south)) * cellsY)));

    for (let ring = 0; ring <= maxRing; ring++) {
      let best = null;
      let bestDist = Infinity;
      const xLo = Math.max(0, cx - ring);
      const xHi = Math.min(cellsX - 1, cx + ring);
      const yLo = Math.max(0, cy - ring);
      const yHi = Math.min(cellsY - 1, cy + ring);
      for (let gy = yLo; gy <= yHi; gy++) {
        for (let gx = xLo; gx <= xHi; gx++) {
          // only scan the newly-added outer ring, not cells already
          // covered by a smaller ring (already searched last iteration)
          const onRingEdge = ring === 0 || gx === xLo || gx === xHi || gy === yLo || gy === yHi;
          if (!onRingEdge) continue;
          const bucket = buckets.get(gy * cellsX + gx);
          if (!bucket) continue;
          for (const p of bucket) {
            const dx = p.lon - lon;
            const dy = p.lat - lat;
            const d = dx * dx + dy * dy;
            if (d < bestDist) {
              bestDist = d;
              best = p;
            }
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  _initParticles() {
    this.particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      this.particles.push({lon: null, lat: null, age: MAX_AGE_FRAMES + 1});
    }
  }

  // Sample uniformly at random over the viewport's *area*, not over its
  // mesh vertices -- picking a random vertex (the old approach) heavily
  // biased spawns toward the coast, where the adaptive mesh has vastly
  // more vertices per unit area than the coarse open-ocean mesh. A few
  // retries with the ring-search lookup handle viewports that are mostly
  // land (few/no data points to land on).
  _respawn(p) {
    const bounds = this.index && this.index.bounds;
    if (!bounds) {
      p.lon = null;
      p.lat = null;
      p.age = MAX_AGE_FRAMES + 1;
      return;
    }
    const [west, south, east, north] = bounds;
    for (let attempt = 0; attempt < 10; attempt++) {
      const lon = west + Math.random() * (east - west);
      const lat = south + Math.random() * (north - south);
      if (this._lookup(lon, lat)) {
        p.lon = lon;
        p.lat = lat;
        p.age = (Math.random() * MAX_AGE_FRAMES) | 0; // stagger ages so particles don't all reset in sync
        p.justRespawned = true;
        return;
      }
    }
    p.lon = null;
    p.lat = null;
    p.age = MAX_AGE_FRAMES + 1;
  }

  _loop() {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(() => this._loop());

    const field = this.getField();
    if (!field) return;

    // index is built/rebuilt asynchronously via invalidateIndex()'s
    // debounce, not synchronously here -- just wait for it.
    if (!this.index) return;

    const viewport = this.deckInstance.getViewports()[0];
    if (!viewport) return;

    const {advect} = SPEED_SCALE[field] || SPEED_SCALE.velocity;
    const alpha = viewport.zoom > HIGH_ZOOM_THRESHOLD ? HIGH_ZOOM_ALPHA : BASE_ALPHA;

    // fade existing trails instead of clearing outright (destination-in
    // keeps only a fraction of the existing alpha each frame)
    this.ctx.globalCompositeOperation = 'destination-in';
    this.ctx.fillStyle = `rgba(0, 0, 0, ${TRAIL_RETENTION})`;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.globalCompositeOperation = 'source-over';

    for (const p of this.particles) {
      if (p.lon === null || p.age > MAX_AGE_FRAMES) {
        this._respawn(p);
        if (p.lon === null) continue;
      }

      const sample = this._lookup(p.lon, p.lat);
      if (!sample) {
        this._respawn(p);
        continue;
      }

      const {u, v} = sample;
      const metersPerDegLon = 111320 * Math.cos((p.lat * Math.PI) / 180);
      const metersPerDegLat = 110540;

      const prevLon = p.lon;
      const prevLat = p.lat;
      p.lon += (u * advect) / metersPerDegLon;
      p.lat += (v * advect) / metersPerDegLat;
      p.age += 1;

      if (!p.justRespawned) {
        const [x0, y0] = viewport.project([prevLon, prevLat]);
        const [x1, y1] = viewport.project([p.lon, p.lat]);
        this.ctx.strokeStyle = traceColor(alpha);
        this.ctx.lineWidth = LINE_WIDTH;
        this.ctx.beginPath();
        this.ctx.moveTo(x0, y0);
        this.ctx.lineTo(x1, y1);
        this.ctx.stroke();
      }
      p.justRespawned = false;
    }
  }
}
